/**
 * The reads behind the impact reading — `impact.ts` is the arithmetic, this is the fetching.
 *
 * Both hooks read a module's RUN DIRECTORY (its newest `IMPACT_RUN_LIMIT` runs, the platform's
 * order), then every row of each run's stored output, and hand back one reading per run. The
 * perimeter is therefore "the newest N runs the directory lists", which is what the screen
 * states above every figure. Nothing is defaulted: with no run in the directory there is no
 * reading, and `runs` is empty.
 *
 * ── WHY A FAN-OUT, AND WHY IT LIVES HERE ─────────────────────────────────────
 *
 * There is no per-run total anywhere in the contract, so a trend over N runs is N collections.
 * `useApiCollection` is one collection per hook call, and React identifies hooks by call order,
 * so a list whose length the directory decides cannot be N calls of it. `useApiQueries` is the
 * list binding, but it reads ONE page per question, and a sum over a first page is the
 * "rendered a first page and called it the answer" failure `useApiCollection` exists to stop.
 *
 * So `useRunCollections` is `useApiQueries`'s shape over `collectPages`, with the same cache
 * key `useApiCollection` uses — a run the markdown screen already read is not read again — and
 * the same retry rule. It is a candidate for promotion into `src/api/` (a `useApiCollections`);
 * until that conversation happens it is private to this chapter rather than copied twice.
 */

import { useQueries, type UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  MAX_PAGES, callOperation, collectPages, shouldRetryRead, useApiCollection, useApiQuery,
  type OperationDescriptor, type ParamsOf,
} from '@/api';
import {
  getMeasurementParameterSet, listConsolidationLines, listConsolidationRuns, listMeasurements, listPriceActions,
  inseasonListRuns,
  type BenefitMeasurement, type ConsolidationLineSummary, type ParameterSetSummary, type PriceActionSummary,
} from '@/api/generated';

import {
  EXIT_LANE_COUNT_QUERIES, IMPACT_ABSENCE, LINE_PAGE_ROWS,
  exitRunImpact, exitRunNotTotalled, exitRunTooLarge, inseasonRunImpact, laneCountsOf,
  type ExitRunImpact, type InseasonRunImpact,
} from './impact';

/**
 * How many runs a module's reading covers — the same ten the Cockpit's run directory offers
 * (`RUN_DIRECTORY_LIMIT` in `src/app/routes/overview/OverviewScreen.tsx`), so the in-season
 * directory read is the SAME request, and one cache entry, on both surfaces.
 */
export const IMPACT_RUN_LIMIT = 10;

/** The platform job whose runs write the consolidation lines — bata-platform ADR 7018/7019. */
export const CONSOLIDATION_RUN_JOB = 'exit.consolidation_run';

/** Rows per page for a run's price steps: the platform's default ceiling, a page size not a cap. */
export const PRICE_ACTION_PAGE_ROWS = 1000;

/** Every row of each of several collections — see the module note. */
function useRunCollections<S extends OperationDescriptor, T>(
  operation: S,
  paramsList: readonly { readonly params: ParamsOf<S>; readonly enabled: boolean }[],
): UseQueryResult<readonly T[], Error>[] {
  const { i18n } = useTranslation();
  const language = i18n.language;
  return useQueries({
    queries: paramsList.map(({ params, enabled }) => ({
      queryKey: [operation.operationId, 'all', params, language],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        collectPages(operation, params, { language, signal, untracked: true }) as Promise<readonly T[]>,
      retry: shouldRetryRead,
      enabled,
    })),
  }) as UseQueryResult<readonly T[], Error>[];
}

/** One page of a run's lines, as far as a count of the list needs it. */
type LaneCountPage = { readonly matching?: number | null };

/**
 * The platform's counts of each run asked for (`EXIT_LANE_COUNT_QUERIES`): one question each, a page of ONE row whose
 * `matching` counts the whole list (ADR 6194). A run not `enabled` is not asked.
 */
function useRunLaneCounts(
  runs: readonly { readonly run_id: string; readonly enabled: boolean }[],
): UseQueryResult<LaneCountPage, Error>[][] {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const results = useQueries({
    queries: runs.flatMap(({ run_id, enabled }) => EXIT_LANE_COUNT_QUERIES.map((narrowing) => {
      const params = { query: { run_id, ...narrowing, limit: 1 } };
      return {
        queryKey: [listConsolidationLines.operationId, 'matching', params, language],
        queryFn: ({ signal }: { signal: AbortSignal }): Promise<LaneCountPage> => (
          callOperation(listConsolidationLines, params, { language, signal })
        ),
        retry: shouldRetryRead,
        enabled,
      };
    })),
  }) as UseQueryResult<LaneCountPage, Error>[];
  const per = EXIT_LANE_COUNT_QUERIES.length;
  return runs.map((_, index) => results.slice(index * per, (index + 1) * per));
}

/**
 * A module's reading, in the four states a screen draws. `ready` carries one reading per run
 * in the directory's order (newest first); an empty list is "no run recorded", a finding.
 */
export type ImpactRead<R> =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly error: unknown }
  | { readonly status: 'ready'; readonly runs: readonly R[] };

/**
 * A reading in the structural query shape `AsyncRegion` takes, so a surface draws the same
 * designed loading, failed and empty states as every other region in the product.
 */
export function asRegionQuery<R>(read: ImpactRead<R>): {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly data: readonly R[] | undefined;
} {
  return {
    isPending: read.status === 'loading',
    isError: read.status === 'error',
    error: read.status === 'error' ? read.error : null,
    data: read.status === 'ready' ? read.runs : undefined,
  };
}

/** Loading until every question has answered; the first refusal is the reading's refusal. */
function settle<R>(
  directory: UseQueryResult<unknown, Error>,
  perRun: readonly UseQueryResult<unknown, Error>[],
  build: () => readonly R[],
): ImpactRead<R> {
  if (directory.isError) return { status: 'error', error: directory.error };
  const failed = perRun.find((query) => query.isError);
  if (failed !== undefined) return { status: 'error', error: failed.error };
  if (directory.isPending || perRun.some((query) => query.isPending && query.fetchStatus !== 'idle')) {
    return { status: 'loading' };
  }
  return { status: 'ready', runs: build() };
}

/** In-season: expected margin recovery per cycle run, over the newest runs. */
export function useInseasonImpact(): ImpactRead<InseasonRunImpact> {
  // The Cockpit's own directory request, verbatim — one cache entry for both.
  const directory = useApiQuery(inseasonListRuns, { query: { limit: IMPACT_RUN_LIMIT } });
  const runs = directory.data?.items ?? [];
  const actions = useRunCollections<typeof listPriceActions, PriceActionSummary>(
    listPriceActions,
    runs.map((run) => ({
      params: { query: { run_id: run.run_id, limit: PRICE_ACTION_PAGE_ROWS } },
      enabled: true,
    })),
  );
  return settle(directory, actions, () => runs.map((run, index) => (
    inseasonRunImpact(run, actions[index]?.data ?? [])
  )));
}

/** Exit: net benefit per consolidation run, over the newest runs. */
export function useExitImpact(): ImpactRead<ExitRunImpact> {
  const directory = useApiQuery(listConsolidationRuns, {
    query: { job_name: CONSOLIDATION_RUN_JOB, limit: IMPACT_RUN_LIMIT },
  });
  const runs = directory.data?.items ?? [];
  const lines = useRunCollections<typeof listConsolidationLines, ConsolidationLineSummary>(
    listConsolidationLines,
    runs.map((run) => ({
      // Proposed lanes only: the benefit is theirs, and a withheld lane carries none. A run
      // past the page ceiling is not asked for at all (`exitRunTooLarge`).
      params: { query: { run_id: run.run_id, is_proposed: true, limit: LINE_PAGE_ROWS } },
      enabled: !exitRunTooLarge(run, MAX_PAGES),
    })),
  );
  // A run too large to total is not asked for its lines, but it is asked how many lanes it holds: the platform counts the list
  // behind the «Priced lanes» card (`matching`), so the card is the true count at any size of run instead of a false zero.
  const counts = useRunLaneCounts(runs.map((run) => ({ run_id: run.run_id, enabled: exitRunTooLarge(run, MAX_PAGES) })));
  return settle(directory, [...lines, ...counts.flat()], () => runs.map((run, index) => (
    exitRunTooLarge(run, MAX_PAGES)
      ? exitRunNotTotalled(run, IMPACT_ABSENCE.tooManyLines, laneCountsOf((counts[index] ?? []).map((query) => query.data)))
      : exitRunImpact(run, lines[index]?.data ?? [])
  )));
}

/**
 * Every programme measurement the platform publishes (`GET /programme/measurements`,
 * bata-platform#431) — `insufficient` rows included, which the platform returns and this does not
 * filter. Unfiltered: the rows name their own market and window, and `benefits.ts` chooses the
 * newest per measure and market. One request for every surface that shows a measured figure.
 */
export function useMeasurements(): UseQueryResult<readonly BenefitMeasurement[], Error> {
  return useApiCollection(listMeasurements, { query: {} });
}

/** The parameter set the measurements were produced under — its pair floor, above all. */
export function useMeasurementParameterSet(): UseQueryResult<ParameterSetSummary | null, Error> {
  return useApiQuery(getMeasurementParameterSet, {});
}
